// styles
import './styles/graphic.scss';
import * as d3 from 'd3';


let chartData;

const margins = {
  top: 20,
  right: 15,
  bottom: 25,
  left: 35,
};

let minValue;
let maxValue;

let minDomain;
let maxDomain;

let lastData;

let latestSeasonLabel;
let previousSeasonCount = 0;
let latestYValue = 0;
let latestXValue = d3.timeParse('%Y-%m-%d')('2020-10-01');
let lastUpdatedTimestamp;

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

export default function (props, container, isMobile) {
  const {
    data,
  } = props;

  if (!data) {
    d3.json('https://ix.cnn.io/data/flu/v2/cdc_flu_hospitalization_overall_all_seasons.json').then((data) => {
      lastUpdatedTimestamp = data.lastUpdatedStr;
      const seasons = [...new Set(data.data.map(d => d.year).sort())];
      chartData = seasons.map(s => ({
        season: s,
        values: data.data.filter(d => d.year == s).map(e => ({
          x: standardizeDate(e),
          y: e.cumulative_rate,
          date: d3.timeParse('%Y-%m-%d')(e.week_ending_date),
        })),
      })).filter(s => s.season != '2009-10');

      minValue = 0;
      maxValue = d3.max(chartData, d => d3.max(d.values, v => v.y));
      minDomain = 40;
      maxDomain = d3.max(chartData, d => d3.max(d.values, v => +v.x));

      const latestSeason = chartData[chartData.length - 1];
      latestSeasonLabel = latestSeason.season;
      previousSeasonCount = chartData.length - 1;

      const currentSeasonDekEl = document.getElementById('chart-season');
      if (currentSeasonDekEl) {
        currentSeasonDekEl.textContent = ` for the ${latestSeasonLabel} season.`;
      }

      const latestValue = latestSeason.values[0];
      latestYValue = latestValue.y;
      latestXValue = latestValue.x;

      lastData = d3.max(chartData, d => d3.max(d.values, v => v.date));
      draw();

      window.onresize = draw;
    });
  }
}

function draw() {
  // console.log(chartData);
  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const xScale = d3.scaleTime()
    .domain([new Date(2020, 9, 1), new Date(2021, 3, 30)])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  const xAxis = d3.axisBottom()
    .scale(xScale)
    .ticks(5)
    .tickFormat(d3.timeFormat('%b'));

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(5)
    .tickSize(-chartWidth);

  const path = d3.line()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.x))
    .y(d => yScale(d.y));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  const lines = g.append('g')
    .attr('class', 'lines')
    .selectAll('.line')
    .data(chartData)
    .enter()
    .append('g')
    .attr('class', 'line');

  lines.append('path')
    .attr('class', d => d.season)
    .attr('stroke', (d) => {
      if (d.season == latestSeasonLabel) { return '#c00'; }
      return '#ccc';
    })
    .attr('stroke-width', (d) => {
      if (d.season == latestSeasonLabel) { return 2.5; }
      return 2;
    })
    .attr('stroke-opacity', (d) => {
      if (d.season == latestSeasonLabel) { return 1; }
      return 0.6;
    })
    .attr('fill', 'none')
    .attr('d', d => path(d.values));


  // Circle
  const circle = g.append('circle')
    .attr('cx', xScale(latestXValue))
    .attr('cy', yScale(latestYValue))
    .attr('r', 3.5)
    .attr('fill', '#C52622')
    .attr('stroke', '#FEFEFE')
    .attr('stroke-width', 1.5);

  d3.select('.grid.y .domain').remove();


  // Annotations
  const annotation = g.append('g')
    .attr('class', 'annotation');

  annotation.append('text')
    .attr('x', xScale(d3.timeParse('%Y-%m-%d')('2021-04-28')))
    .attr('y', yScale(22))
    .attr('text-anchor', 'end')
    .attr('class', 'annotation-previous')
    .text(() => {
      if (previousSeasonCount > 0) {
        return `${previousSeasonCount.toString()} previous seasons`;
      }
      return 'Previous seasons';
    });


  // Position annotation to the right if early-ish in season, left if later
  const isEarlySeason = xScale(latestXValue) < xScale(d3.timeParse('%Y-%m-%d')('2021-03-01'));
  const textAnchor = isEarlySeason ? 'start' : 'end';
  const textX = isEarlySeason ? (xScale(latestXValue) + 8) : (xScale(latestXValue) + 4);
  const textDY = isEarlySeason ? 2 : -40;

  annotation.append('text')
    .attr('x', textX)
    .attr('y', yScale(Math.max(latestYValue, 20)))
    .attr('dx', 0)
    .attr('dy', textDY)
    .attr('text-anchor', textAnchor)
    .attr('class', 'annotation-current')
    .text(latestYValue);

  annotation.append('text')
    .attr('x', textX)
    .attr('y', yScale(Math.max(latestYValue, 20)))
    .attr('dx', 0)
    .attr('dy', textDY + 15)
    .attr('text-anchor', textAnchor)
    .attr('class', 'annotation-current-week')
    .text(() => (isMobile ? 'Week ending' : `Week ending ${d3.timeFormat('%b %-d, %Y')(lastData)}`));

  if (isMobile) {
    annotation.append('text')
      .attr('x', textX)
      .attr('y', yScale(Math.max(latestYValue, 20)))
      .attr('dx', 0)
      .attr('dy', textDY + 30)
      .attr('text-anchor', textAnchor)
      .attr('class', 'annotation-current-week')
      .text(`${d3.timeFormat('%b %-d, %Y')(lastData)}`);
  }

  d3.select('#timestamp')
    .text(`Data updated weekly. Last updated ${d3.timeFormat('%B %-d, %Y')(lastData)}. Flu season surveillance typically begins in October and continues through the following April.`);

  window.pymChild.sendHeight();
}


function standardizeDate(dataPoint) {
  const date = d3.timeParse('%Y-%m-%d')(dataPoint.week_ending_date);

  const startYear = parseInt(dataPoint.year.split('-')[0]);

  if (date.getFullYear() == startYear) {
    date.setFullYear(2020);
  } else {
    date.setFullYear(2021);
  }

  return date;
}
